import { useReduxState } from 'hooks';
import { getBlockNameByType } from 'lib/blocks';
import { getAllLanguageOptionsByLangCode } from 'lib/courses';
import {
  ContentBox,
  FlagIcon,
  LinkButton,
  MoreOptions,
  User
} from 'components/common';
import { EditedContent } from 'components/common/notifications';
import { defaultBlockData } from 'components/editor/blocks';
import {
  AnswerDelete,
  FillInAnswer,
  H5PAnswer,
  LikertAnswer,
  MultipleChoiceAnswer,
  NumericQuestionAnswer,
  OpenQuestionAnswer,
  Reviews,
  ScormAnswer,
  SequencingAnswer,
  TaskQuestion
} from '.';

import type { Event, TaskAnswerReview, UserWithBasicData } from 'shared-types';

interface Props {
  answer: any;
  courseId: number;
  courseSlug: string;
  events?: Pick<Event, 'createdAt' | 'id' | 'type'>[];
  isAdmin?: boolean;
  onDataChange?: () => Promise<void>;
  reviews?: TaskAnswerReview[];
  showPreview?: boolean;
  showQuestion?: boolean;
  task: any;
  user: UserWithBasicData;
}

const Answer = ({
  answer,
  courseId,
  courseSlug,
  events,
  isAdmin,
  onDataChange,
  reviews,
  showPreview,
  showQuestion = true,
  task,
  user
}: Props) => {
  const {
    messages,
    pageState: { course },
    theme,
    user: loggedInUser
  } = useReduxState();

  const languageOptions = getAllLanguageOptionsByLangCode();

  const maxScore = task.data.hasOwnProperty('pointsWeight')
    ? task.data.pointsWeight
    : defaultBlockData[task.type].pointsWeight;

  const h5pVersion2 = task.type === 'h5p' && task.data?.version > 1;

  return (
    <div className="answer">
      {showQuestion ? (
        <>
          <div className="title">
            <span>
              {getBlockNameByType({
                messages,
                type: task.data?.isSingleChoice ? 'singlechoice' : task.type
              })}
            </span>

            {h5pVersion2 ? <span>{' (v2)'}</span> : null}

            {task.data?.isMandatory ? (
              <span className="title-mandatory">
                <span className="title-divider">{'•'}</span>
                <span>{messages.commonMandatory}</span>
              </span>
            ) : null}

            {isAdmin ? (
              <LinkButton
                category="lighter"
                href={`/admin/courses/${courseSlug}/edit/${task.chapter.slug}#${task.id}`}
                icon="pen"
                iconStyle={{
                  fontSize: '0.9rem',
                  position: 'relative',
                  top: -1
                }}
                isRounded={true}
                size="small"
                style={{ marginLeft: 10, padding: '4px 9px 5px' }}
                text={messages.commonEdit}
              />
            ) : null}
          </div>

          <div className="question">
            <TaskQuestion
              question={
                task.type === 'likert'
                  ? task.data.description
                  : task.data.question
              }
            />
          </div>
        </>
      ) : null}

      <ContentBox>
        <div className="header">
          <div className="user">
            <User
              enableProfileLink={loggedInUser?.roles?.includes('admin')}
              showEmailTooltip={isAdmin}
              size="small"
              user={user}
              {...(answer.createdAt && answer.updatedAt
                ? {
                    subtitle: (
                      <EditedContent
                        createdAt={answer.createdAt}
                        updatedAt={answer.updatedAt}
                      />
                    )
                  }
                : { date: answer.createdAt })}
            />
            {answer?.courseLanguageVersion &&
              Object.keys(course.languageVersions).length > 0 &&
              (() => {
                const languageOption =
                  languageOptions[answer.courseLanguageVersion];

                return (
                  <FlagIcon
                    lang={answer.courseLanguageVersion}
                    style={{ marginLeft: 10 }}
                    tooltipText={
                      messages.courseLanguageVersion +
                      ': ' +
                      languageOption?.capitalizedName
                    }
                  />
                );
              })()}
          </div>

          {isAdmin ? (
            <div className="dropdown">
              <MoreOptions category="lighter">
                <AnswerDelete
                  answer={answer}
                  courseId={courseId}
                  onDataChange={onDataChange}
                />
              </MoreOptions>
            </div>
          ) : null}
        </div>

        {task.type === 'multiplechoice' ? (
          <MultipleChoiceAnswer
            answers={answer.data.answers}
            maxScore={maxScore}
            options={task.data.options}
            score={answer.score}
          />
        ) : task.type === 'openquestion' ? (
          <OpenQuestionAnswer
            filename={answer.data.filename}
            filepath={answer.data.filepath}
            maxScore={maxScore}
            score={answer.score}
            text={answer.data.text}
          />
        ) : task.type === 'numericquestion' ? (
          <NumericQuestionAnswer
            answer={answer.data.answer}
            maxScore={maxScore}
            score={answer.score}
          />
        ) : task.type === 'fillin' ? (
          <FillInAnswer
            answers={answer.data.answers}
            contentData={task.data.contentData}
            correctAnswers={answer.correctAnswers}
            maxScore={maxScore}
            score={answer.score}
          />
        ) : task.type === 'h5p' ? (
          <H5PAnswer
            answers={answer.data.userAnswers || {}}
            completion={answer.data.completion}
            maxScore={maxScore}
            missingCorrectAnswers={answer.data.missingCorrectAnswers}
            success={answer.data.success}
            score={answer.data.score}
            points={answer.score}
            task={task}
            showPreview={showPreview}
          />
        ) : task.type === 'likert' ? (
          <LikertAnswer
            answers={answer.data.answers}
            questions={task.data.questions}
            options={task.data.options}
          />
        ) : task.type === 'scorm' ? (
          <ScormAnswer
            completion={answer.data.completion}
            maxScore={maxScore}
            success={answer.data.success}
            score={answer.data.score}
            points={answer.score}
            task={task}
            showPreview={showPreview}
          />
        ) : task.type === 'sequencing' ? (
          <SequencingAnswer
            answers={answer.data.answers}
            maxScore={maxScore}
            options={task.data.options}
            score={answer.score}
          />
        ) : null}

        {task.type === 'openquestion' && task.data ? (
          <Reviews
            answerEvents={events}
            answerId={answer.id}
            answerUser={user}
            courseId={courseId}
            gradingType={task.data.gradingType}
            isAdmin={isAdmin}
            isTaskEditable={task.data.allowEditing}
            maxPoints={maxScore}
            onDataChange={onDataChange}
            onlyComments={task.data.grading === 'none'}
            reviews={reviews}
          />
        ) : null}
      </ContentBox>

      <style jsx>{`
        .title {
          color: ${theme.textColorLight};
          flex-grow: 1;
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 15px;
          min-width: 0;
        }
        .title-mandatory {
          color: ${theme.textColorLighter};
          font-weight: 400;
        }
        .title-divider {
          margin: 0 5px;
        }
        .title-points {
          margin-left: 10px;
        }
        .header {
          align-items: center;
          display: flex;
          margin-bottom: 20px;
        }
        .user {
          flex-grow: 1;
        }
        .dropdown {
          margin-left: 15px;
        }
      `}</style>
    </div>
  );
};

export default Answer;
